import {variableExample} from "../model/resolution";
import type {PickerVariable, VariablePreview, VariableScopeIcon} from "../model/types";

/**
 * One row of the picker, whatever it lists.
 *
 * The panel knows rows, not variables: a host with something else to offer (a printout's block
 * fields, a survey's answers) maps its own data onto this shape and gets the same list, card and
 * keyboard handling without the panel learning about it.
 */
export interface PickerItem {
    /** What the row identifies; the stable React key and option id. */
    key: string;
    /** Text in the row chip; defaults to `key`. */
    chipText?: string;
    label: string;
    /** Info card text. */
    description?: string;
    /** Rows group under the first occurrence of this key, whether or not they are consecutive. */
    groupKey?: string;
    /** Heading text; defaults to `groupKey`. */
    groupLabel?: string;
    icon?: VariableScopeIcon;
    /** `undefined` → no Source row; `null` → the value is derived, and the card says so. */
    source?: string | null;
    /** Lazy, because resolving every example on each keystroke is work nobody looks at. */
    example?: () => string;
    /**
     * What `matchesPickerItem` searches; defaults to key and label. Join several fields with "\n":
     * neither query can hold a line break (the "/" query stops at a space, the search input is one
     * line), so a match never runs across two fields — each is searched on its own, as before.
     */
    searchText?: string;
}

export type VariablePickerItem = PickerItem & {definition: PickerVariable};

/** The catalogue as picker rows: grouped by scope, searchable by key, label and scope as before. */
export function pickerItemsFor(
    definitions: readonly PickerVariable[],
    preview: VariablePreview,
): VariablePickerItem[] {
    return definitions.map(definition => ({
        key: definition.key,
        label: definition.label,
        description: definition.description,
        groupKey: definition.scopeKey,
        groupLabel: definition.scope,
        icon: definition.icon,
        source: definition.source ?? definition.path ?? definition.compute ?? null,
        example: () => variableExample(definition, preview),
        searchText: [definition.key, definition.label, definition.scope].join("\n"),
        definition,
    }));
}

/** Case-insensitive substring match; an empty query matches everything. */
export function matchesPickerItem(item: PickerItem, query: string): boolean {
    const needle = query.trim().toLowerCase();

    return !needle || (item.searchText ?? `${item.key}\n${item.label}`).toLowerCase().includes(needle);
}

export default pickerItemsFor;
